import { useState, useEffect, useRef, useMemo } from "react";
import {
  TextField,
  Autocomplete,
  CircularProgress,
  Box,
  Typography,
} from "@mui/material";

/** Place types returned by Places API relevant to rail / major stations (language-agnostic). */
const RAIL_RELEVANT_TYPES = new Set([
  "train_station",
  "transit_station",
  "subway_station",
  "light_rail_station",
]);

function predictionHasRailType(prediction) {
  const types = prediction?.types;
  if (!Array.isArray(types) || types.length === 0) return true;
  return types.some((t) => RAIL_RELEVANT_TYPES.has(t));
}

const GoogleMapsAutocompleteRail = ({
  title = "Location",
  onPlaceSelect,
  placeholder = "Enter location",
  error,
  type = ["geocode"],
  value,
  disable = false,
  titleDisabled = false,
  railMode = false,
}) => {
  const [inputValue, setInputValue] = useState(value || "");
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [userLocation, setUserLocation] = useState(null);
  const serviceRef = useRef(null);

  // Get user's current location on component mount
  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setUserLocation({
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          });
        },
        (error) => {
          console.error("Error getting user location:", error);
        }
      );
    }
  }, []);

  useEffect(() => {
    if (value !== inputValue) {
      setInputValue(value || "");
    }
    // Intentionally only when `value` prop changes — do not depend on `inputValue` or typing resets.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  useEffect(() => {
    if (!window.google) return;

    if (!serviceRef.current) {
      serviceRef.current = new window.google.maps.places.AutocompleteService();
    }
  }, []);

  const requestTypes = useMemo(
    () =>
      railMode
        ? ["train_station", "transit_station", "subway_station"]
        : type,
    [railMode, type],
  );

  useEffect(() => {
    let active = true;
    let debounceId;

    if (!inputValue?.trim()) {
      setOptions([]);
      setLoading(false);
      return undefined;
    }

    if (!serviceRef.current) return undefined;

    debounceId = window.setTimeout(() => {
      setLoading(true);

      const request = {
        input: inputValue.trim(),
        types: requestTypes,
      };

      if (userLocation) {
        request.location = new window.google.maps.LatLng(
          userLocation.lat,
          userLocation.lng,
        );
        request.radius = 50000;
        request.strictBounds = false;
      }

      serviceRef.current.getPlacePredictions(request, (predictions, status) => {
        if (!active) return;
        setLoading(false);
        if (
          status !== window.google.maps.places.PlacesServiceStatus.OK ||
          !predictions?.length
        ) {
          setOptions([]);
          return;
        }
        console.log("predictions", predictions, railMode);
        const filtered = railMode
          ? predictions.filter(predictionHasRailType)
          : predictions;

        if (userLocation && filtered.length > 1) {
          filtered.sort(
            (a, b) =>
              (a.distance_meters ?? Number.POSITIVE_INFINITY) -
              (b.distance_meters ?? Number.POSITIVE_INFINITY),
          );
        }

        setOptions(filtered);
      });
    }, 250);

    return () => {
      active = false;
      window.clearTimeout(debounceId);
    };
  }, [inputValue, requestTypes, userLocation]);

  const fetchPlaceDetails = (placeId) => {
    if (!window.google) return;

    const placesService = new window.google.maps.places.PlacesService(
      document.createElement("div")
    );

    placesService.getDetails(
      {
        placeId,
        fields: ["name", "formatted_address", "geometry", "address_components"],
      },
      (place, status) => {
        if (status === window.google.maps.places.PlacesServiceStatus.OK) {
          if (onPlaceSelect) {
            const { formatted_address, name, address_components, geometry } =
              place;
            const lat = geometry?.location?.lat();
            const lng = geometry?.location?.lng();
            onPlaceSelect({
              name,
              formatted_address,
              address_components,
              location: {
                lat,
                lng,
              },
            });
          }
        }
      }
    );
  };

  return (
    <Box>
      {!titleDisabled && (
        <Typography
          variant="subtitle1"
          sx={{
            fontWeight: "bold",
            mt: 1,
          }}
        >
          {title}
        </Typography>
      )}

      <Autocomplete
        freeSolo
        inputValue={inputValue}
        filterOptions={(x) => x}
        disabled={disable}
        options={options}
        getOptionLabel={(option) => option.description || option.name || ""}
        noOptionsText="No locations"
        loading={loading}
        popupIcon={null}
        value={value ? { description: value } : null}
        onInputChange={(event, newInputValue) => {
          setInputValue(newInputValue);
          if (newInputValue === "") {
            onPlaceSelect(null);
          }
        }}
        onChange={(event, newValue) => {
          if (newValue?.place_id) {
            fetchPlaceDetails(newValue.place_id);
          } else if (newValue === null) {
            onPlaceSelect(null);
          }
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder={placeholder}
            variant="outlined"
            size="small"
            helperText={
              error && (
                <Typography
                  variant="body2"
                  color="error"
                  style={{
                    marginLeft: "-12px",
                    marginTop: "-5px",
                  }}
                >
                  {!titleDisabled && error}
                </Typography>
              )
            }
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading ? (
                    <CircularProgress color="inherit" size={20} />
                  ) : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
            fullWidth
          />
        )}
      />
    </Box>
  );
};

export default GoogleMapsAutocompleteRail;
